import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API, TOKEN_KEY } from "@/lib/api";

const Ctx = createContext({ unread: 0, subscribe: () => () => {}, refreshUnread: () => {}, enablePush: async () => {} });
export const useChat = () => useContext(Ctx);

const b64 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const r = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...r].map((c) => c.charCodeAt(0))); };

export async function enablePush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return "denied";
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/chat/push/key");
  const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64(data.key) });
  const j = sub.toJSON();
  await api.post("/chat/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
  return "ok";
}

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const subs = useRef(new Set());
  const loc = useLocation();
  const nav = useNavigate();
  const locRef = useRef(loc.pathname);
  locRef.current = loc.pathname;
  const refreshUnread = useCallback(() => {
    if (!user) return;
    api.get("/chat/rooms").then((r) => setUnread(r.data.reduce((s, x) => s + x.unread, 0))).catch(() => {});
  }, [user]);
  const subscribe = useCallback((fn) => { subs.current.add(fn); return () => subs.current.delete(fn); }, []);

  useEffect(() => {
    if (!user) return undefined;
    refreshUnread();
    let ws, alive = true, retry;
    const connect = () => {
      const url = `${API.replace(/^http/, "ws")}/ws/chat?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;
      ws = new WebSocket(url);
      ws.onmessage = (e) => {
        const ev = JSON.parse(e.data);
        subs.current.forEach((fn) => fn(ev));
        if (ev.type === "message" && ev.message.sender_id !== user.id) {
          if (!locRef.current.startsWith("/chat")) {
            setUnread((u) => u + 1);
            toast(`${ev.message.sender_name}`, { description: ev.message.text || "📎 ملف جديد", action: { label: "فتح", onClick: () => nav(`/chat?room=${ev.room_id}`) } });
          }
          if (document.hidden && "Notification" in window && Notification.permission === "granted") {
            new Notification(ev.message.sender_name, { body: ev.message.text || "ملف جديد", tag: ev.room_id });
          }
        }
      };
      ws.onclose = () => { if (alive) retry = setTimeout(connect, 4000); };
    };
    connect();
    const poll = setInterval(refreshUnread, 60000);
    return () => { alive = false; clearTimeout(retry); clearInterval(poll); ws?.close(); };
  }, [user, refreshUnread, nav]);

  return <Ctx.Provider value={{ unread, setUnread, subscribe, refreshUnread, enablePush }}>{children}</Ctx.Provider>;
}
